/**
 * The TanStack Table features every table in the app is built with.
 *
 * TanStack v9 types columns and tables by their feature set, so all tables
 * share one `AppFeatures` type and column definitions stay interchangeable.
 * Only `pagedFeatures` adds the client-side paginated row model; every other
 * table renders all of its rows, as it did under v8.
 */

import {
  columnFilteringFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  globalFilteringFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
  type ColumnDef as TanStackColumnDef,
  type FilterFn as TanStackFilterFn,
  type RowData,
} from '@tanstack/react-table';

const baseFeatures = {
  columnFilteringFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  globalFilteringFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  // The built-ins v8's `'auto'` sorting picked from; v9 only uses registered ones.
  sortFns: { alphanumeric: sortFn_alphanumeric, datetime: sortFn_datetime, text: sortFn_text },
  // Type-only: the `meta` every column may carry (read by DataTable).
  columnMeta: {} as { sticky?: 'left' | 'right' },
};

export type AppFeatures = typeof baseFeatures & {
  paginatedRowModel?: ReturnType<typeof createPaginatedRowModel>;
};

export type ColumnDef<TData extends RowData> = TanStackColumnDef<AppFeatures, TData>;
export type FilterFn<TData extends RowData> = TanStackFilterFn<AppFeatures, TData>;

export const features: AppFeatures = tableFeatures(baseFeatures);

/** For tables that page in the browser rather than on the server. */
export const pagedFeatures: AppFeatures = tableFeatures({
  ...baseFeatures,
  paginatedRowModel: createPaginatedRowModel(),
});
